import {useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {v4} from 'uuid';

import useConfig from '../useConfig';
import useRestAPI from '../useRestAPI';
import {createQueryParam} from '../../functions';
import {CountryCodeType, LanguageType} from '../../model';
import {MBIDAuthCompleteResponse} from '../../services/rest-api/MBIDAuthService/MBIDAuthService';
import {analytics} from '@resurs-internal/core/analytics';
import {useAuthGlobalState} from './useAuthGlobalState';
import RefreshAuthRequest from '../../model/RefreshAuthRequest';
import {createPKCE} from './createPKCE';
import useEntryPoint from '../useEntryPoint';
import {OAuth2AuthenticateResponse} from '../../services/rest-api/OAuth2Service/OAuth2Service';
import {
  decodeAccessToken,
  decodeRefreshToken,
} from '@resurs-internal/core/lib/jwt';
import {ensure} from '../../helpers/ensure';
import {endpoints} from '../../services/endpoints';

export interface AuthURLOptions {
  redirectUri: string;
  language: LanguageType;
  countryCode: CountryCodeType;
  scope: string;
  loginHint?: string;
}

export const useAuthState = (entrypointCountry?: CountryCodeType) => {
  const auth = useAuthGlobalState();
  const config = useConfig();
  const client = useQueryClient();
  const api = useRestAPI();
  const entrypoint = useEntryPoint(entrypointCountry);

  const removeQueries = useCallback(() => {
    client.removeQueries({
      predicate: (qry) => {
        return persistKeys.every((key) => {
          const persistKey = key.join('/');
          const queryKey = qry.queryKey.join('/');
          return !queryKey.includes(persistKey);
        });
      },
    });
  }, [client]);

  const signInWithMBID = useMutation({
    mutationKey: ['auth', 'mbid'],
    mutationFn: async (governmentId?: string) => {
      analytics.log('login_mbid');
      auth.update({
        isLoggedIn: false,
        isLoggingIn: true,
        token: undefined,
        refreshToken: undefined,
        stateId: undefined,
        autoStartToken: undefined,
        loginToken: undefined,
        codeVerifier: undefined,
        links: undefined,
        refreshExpireAt: undefined,
        loginType: undefined,
        clientId: undefined,
      });

      const entry = await entrypoint.query.refetch();
      if (!entry.isSuccess) {
        const responseBody = JSON.stringify(entry.error ?? entry.data, null, 2);
        throw new Error(`MBID entrypoint request failed ${responseBody}`);
      }
      const url = `${entry.data._links.mbidAuthentication.href}?authBE=dids`;
      const response = await api.mbidAuth.authenticate(url, {
        endUserIp: '127.0.0.1',
        governmentId: governmentId,
        token: entry.data?.accessToken ?? '',
      });
      auth.update({
        isLoggedIn: false,
        isLoggingIn: true,
        token: response.accessToken,
        refreshToken: response.refreshToken,
        autoStartToken: response.autoStartToken,
        loginToken: response.loginToken,
      });
      return response;
    },
  });

  const signInWithMBIDToken = useMutation({
    mutationKey: ['auth', 'mbid', 'token'],
    mutationFn: async (result: MBIDAuthCompleteResponse) => {
      ensure(result.accessToken, 'signInMBID: accessToken undefined');
      ensure(result.refreshToken, 'signInMBID: refreshToken undefined');
      analytics.login(result.accessToken);
      const refreshExpireAtSeconds =
        decodeRefreshToken(result.refreshToken)?.exp ?? 0;
      const refreshExpireAt = refreshExpireAtSeconds * 1000;
      removeQueries();
      auth.update({
        loginType: 'mbid',
        isLoggedIn: true,
        isLoggingIn: false,
        token: result.accessToken,
        refreshToken: result.refreshToken,
        refreshExpireAt: refreshExpireAt,
        links: result._links,
        serverTimeOffset: getServerTimeOffset(result.accessToken),
      });
    },
  });

  const createAuthRedirectURL = useCallback(
    (options: AuthURLOptions) => {
      const stateID = v4();
      const host = config.links.oAuthRedirect;
      const {codeVerifier, codeChallenge, codeMethod} = createPKCE();

      auth.update({
        isLoggedIn: false,
        isLoggingIn: true,
        stateId: stateID,
        codeVerifier: codeVerifier,
      });

      const params = createQueryParam({
        client_id: config?.clientId,
        redirect_uri: options.redirectUri,
        locale: `${options.language}_${options.countryCode}`,
        scope: `${options.scope}`,
        state: stateID,
        hl: `${options.countryCode}`,
        login_hint: options.loginHint,
        response_type: 'code',
        code_challenge: codeChallenge,
        code_challenge_method: codeMethod,
      });
      return `${host}${params}`;
    },
    [auth, config],
  );

  const signInWithCode = useMutation({
    mutationKey: ['auth', 'code'],
    mutationFn: async (params: {
      code: string;
      state?: string;
      codeVerifier?: string;
    }) => {
      // const stateParam = params?.state || undefined;
      // const stateId = auth.state.stateId || undefined;
      // if (stateParam !== stateId && stateParam !== 'auth-redirect') {
      //   // stateId is undefined if oauth2 flow started in different browser
      //   throw new Error(`Invalid State, expected ${stateId} got ${stateParam}`);
      // }

      analytics.log('login_oauth2');
      const url = config.links.oAuthToken;
      const response = await api.client.fetchJSON('POST', url, {
        client_id: config.clientId,
        grant_type: 'code',
        code: params.code,
        code_verifier: params.codeVerifier ?? auth.state.codeVerifier,
      });
      const responseBody =
        await api.client.decodeJSON<OAuth2AuthenticateResponse>(response);

      if (!responseBody?.access_token?.length) {
        const status = `[${response.status}]: ${response.statusText}`;
        const body = `${JSON.stringify(responseBody)}`;
        throw new Error(`Invalid access token\n${status}\n${body}`);
      }
      analytics.login(responseBody.access_token);
      removeQueries();

      const nowMillis = new Date().getTime();
      const refreshExpireIn = responseBody.refresh_token_expires_in * 1000;
      auth.update({
        isLoggedIn: true,
        isLoggingIn: false,
        loginType: 'oauth2',
        token: responseBody.access_token,
        refreshToken: responseBody.refresh_token,
        refreshExpireAt: nowMillis + refreshExpireIn,
        links: responseBody._links,
        serverTimeOffset: getServerTimeOffset(responseBody.access_token),
      });

      return responseBody;
    },
  });

  const signTNC = useCallback(async () => {
    analytics.log('accept_tnc');
    removeQueries();
    const requestUrl = auth.state?.links?.signTermsAndConditions?.href;
    const response = await api.mbidAuth.signTNC(requestUrl, auth.state?.token);
    const refreshExpireAt =
      (decodeRefreshToken(response.refreshToken)?.exp ?? 0) * 1000;
    auth.update({
      isLoggedIn: true,
      isLoggingIn: false,
      loginType: 'mbid',
      token: response.accessToken,
      refreshToken: response.refreshToken,
      refreshExpireAt: refreshExpireAt,
      links: response._links,
      serverTimeOffset: getServerTimeOffset(response.accessToken),
    });

    return response;
  }, [auth, api.mbidAuth, removeQueries]);

  const signInWithToken = useCallback(
    async (request: RefreshAuthRequest) => {
      auth.update({clientId: request.clientId});
      return await api.client.refreshAuth(request);
    },
    [api.client, auth],
  );

  const refresh = useCallback(async () => {
    return await api.client.refreshAuth();
  }, [api.client]);

  const signOut = useMutation({
    mutationKey: ['auth', 'sign-out'],
    mutationFn: async () => {
      analytics.logout();

      // revoke oauth/signicat refresh token
      if (auth.state.loginType === 'oauth2' && auth.state.refreshToken) {
        const url = endpoints.auth.revokeOAuthRefreshToken.build({
          query: {
            tokenTypeHint: 'refresh_token',
            token: auth.state.refreshToken,
          },
        });
        await api.client
          .fetchJSON(
            'POST',
            url,
            {tokenTypeHint: 'refresh_token', token: auth.state.refreshToken},
            {'Content-Type': 'application/x-www-form-urlencoded'},
          )
          .catch((error) => {
            console.error('Failed to revoke oauth refresh token:', error);
          });
      }

      // revoke mbid/bankid refresh token
      if (auth.state.loginType === 'mbid' && auth.state.refreshToken) {
        const token = decodeRefreshToken(auth.state.refreshToken);
        const url = endpoints.auth.revokeMBIDRefreshToken.build({
          params: {jti: `${token?.jti}`},
        });
        await api.client.fetchJSON('POST', url).catch((error) => {
          console.error('Failed to revoke mbid refresh token:', error);
        });
      }

      removeQueries();
      auth.reset();
    },
  });

  const getPreauthorizationURL = useCallback(
    (id: string, status: string, bpt?: string, reason?: string) => {
      const query = createQueryParam({bpt, reason});
      return `${config.links.oAuthPreAuth}/${id}/${status}${query}`;
    },
    [config.links.oAuthPreAuth],
  );

  const getEntrypointAccessToken = useMutation({
    mutationKey: ['auth', 'entrypoint-token'],
    mutationFn: async (): Promise<{accessToken: string}> => {
      analytics.log('get_entrypoint_token');

      const entry = await entrypoint.query.refetch();
      if (!entry.isSuccess) {
        const responseBody = JSON.stringify(entry.error ?? entry.data, null, 2);
        throw new Error(`Entrypoint request failed: ${responseBody}`);
      }

      const accessToken = entry.data?.accessToken;
      if (!accessToken) {
        throw new Error('No access token received from entrypoint');
      }

      return {accessToken};
    },
  });

  return {
    state: auth.state,
    signInWithMBID,
    signInWithMBIDToken,
    createAuthRedirectURL,
    signInWithCode,
    signInWithToken,
    signTNC,
    refresh,
    signOut,
    getPreauthorizationURL,
    getEntrypointAccessToken,
  };
};

export const persistKeys = [
  ['auth'],
  ['bankid'],
  ['themeMode'],
  ['mock'],
  ['language'],
  ['chat', 'state'],
  ['cart'],
];

function getServerTimeOffset(token: string) {
  const decodedToken = decodeAccessToken(token);
  const issuedAtMs = (decodedToken?.iat || 0) * 1_000;
  return Date.now() - issuedAtMs;
}

export default useAuthState;
